import React, { useEffect, useState } from "react";
import { Ruler, Sparkles, X } from "lucide-react";
import api from "@/lib/api";

const FITS = [["snug", "Snug", -3], ["regular", "Regular", 0], ["relaxed", "Relaxed", 4]];
const INPUT_CLS = "mt-1 w-full rounded-2xl border-2 border-[#19324A]/10 bg-white px-4 py-3 text-lg font-display font-bold text-[#19324A] outline-none focus:border-[#FFD93D]";
const estimateChest = (h, w) => 0.2 * h + 0.55 * w + 22;

function pickSize(rows, value) {
  if (!rows.length) return null;
  const hit = rows.find((r) => value >= r.min && value <= r.max);
  if (hit) return { size: hit.size, exact: true };
  if (value < rows[0].min) return { size: rows[0].size, exact: false, edge: "small" };
  if (value > rows[rows.length - 1].max) return { size: rows[rows.length - 1].size, exact: false, edge: "large" };
  const next = rows.find((r) => value < r.min);
  return { size: next ? next.size : rows[rows.length - 1].size, exact: false };
}

// Pure sizing logic: turns the inputs + guide into a recommendation (or null until enough is entered).
export function recommendSize({ guide, sizes, height, weight, fit }) {
  const rows = guide.rows.filter((r) => !sizes.length || sizes.includes(r.size));
  const h = parseFloat(height), w = parseFloat(weight);
  const isKids = guide.measure === "height";
  const ready = isKids ? h > 60 : h > 100 && w > 25;
  const value = isKids ? h : estimateChest(h, w) + (FITS.find((f) => f[0] === fit)?.[2] || 0);
  return { isKids, value, rec: ready ? pickSize(rows, value) : null };
}

const recNote = (rec, isKids, value, fit) => [
  isKids ? `Based on height ${Math.round(value)} cm` : `Est. chest ~${Math.round(value)} cm · ${fit} fit`,
  rec.width && ` · tee ${rec.width} cm wide, ${rec.length} cm long (flat)`,
  rec.edge === "large" && " · our biggest size, may be snug",
  rec.edge === "small" && " · our smallest size, may be roomy",
  !rec.exact && !rec.edge && " · between sizes, we sized up",
].filter(Boolean).join("");

function SizeInputs({ isKids, height, weight, fit, setHeight, setWeight, setFit }) {
  return (
    <>
      <div className={`grid ${isKids ? "grid-cols-1" : "grid-cols-2"} gap-3 mt-5`}>
        <label className="block"><span className="text-xs font-semibold text-[#19324A]/60">{isKids ? "Child's height (cm)" : "Height (cm)"}</span>
          <input type="number" inputMode="numeric" value={height} onChange={(e) => setHeight(e.target.value)} placeholder={isKids ? "e.g. 118" : "e.g. 172"} data-testid="size-height" className={INPUT_CLS} /></label>
        {!isKids && (
          <label className="block"><span className="text-xs font-semibold text-[#19324A]/60">Weight (kg)</span>
            <input type="number" inputMode="numeric" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="e.g. 70" data-testid="size-weight" className={INPUT_CLS} /></label>
        )}
      </div>
      {!isKids && (
        <div className="mt-4">
          <span className="text-xs font-semibold text-[#19324A]/60">How do you like it to fit?</span>
          <div className="flex gap-2 mt-1">
            {FITS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFit(k)} data-testid={`fit-${k}`}
                className={`flex-1 rounded-full px-3 py-2 text-sm font-display font-bold border-2 transition-colors ${fit === k ? "bg-[#FFD93D] border-[#FFD93D] text-[#19324A]" : "bg-white border-[#19324A]/10 text-[#19324A]/70 hover:border-[#FFD93D]"}`}>{label}</button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function SizeResult({ rec, isKids, value, fit, onPick }) {
  return (
    <div className="mt-5 rounded-3xl bg-white p-5 text-center min-h-[120px] flex flex-col items-center justify-center" data-testid="size-result">
      {rec ? (
        <>
          <p className="text-xs font-semibold text-[#19324A]/55 uppercase tracking-wide">We'd go with</p>
          <p className="font-display font-extrabold text-5xl text-[#19324A] my-1" data-testid="size-result-value">{rec.size}</p>
          <p className="text-xs text-[#19324A]/60">{recNote(rec, isKids, value, fit)}</p>
          <button type="button" onClick={() => onPick(rec.size)} data-testid="size-result-pick" className="btn-pink !py-2 mt-3">Choose {rec.size}</button>
        </>
      ) : (
        <p className="text-sm text-[#19324A]/55 inline-flex items-center gap-2"><Ruler className="w-4 h-4" /> Pop in your {isKids ? "child's height" : "height and weight"} and we'll suggest a size.</p>
      )}
    </div>
  );
}

export default function SizeRecommender({ productId, sizes = [], onPick }) {
  const [open, setOpen] = useState(false);
  const [guide, setGuide] = useState(null);
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [fit, setFit] = useState("regular");

  useEffect(() => {
    setGuide(null);
    api.get(`/products/${productId}/size-guide`).then(({ data }) => setGuide(data)).catch(() => setGuide(false));
  }, [productId]);

  if (!guide) return null;
  const { isKids, value, rec } = recommendSize({ guide, sizes, height, weight, fit });

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="find-my-size-btn" className="text-sm text-[#FF6B9D] font-semibold inline-flex items-center gap-1 hover:underline">
        <Sparkles className="w-4 h-4" /> Find my size
      </button>
      {open && (
        <div className="fixed inset-0 z-[70] bg-[#19324A]/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={() => setOpen(false)} data-testid="size-recommender">
          <div className="bg-[#FFF9EE] w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 soft-shadow" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-hand text-xl text-[#FF6B9D]">Quick fit check</p>
                <h3 className="font-display font-extrabold text-2xl text-[#19324A] leading-tight">Find my size</h3>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="size-recommender-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center"><X className="w-5 h-5 text-[#19324A]" /></button>
            </div>
            <SizeInputs isKids={isKids} height={height} weight={weight} fit={fit} setHeight={setHeight} setWeight={setWeight} setFit={setFit} />
            <SizeResult rec={rec} isKids={isKids} value={value} fit={fit} onPick={(s) => { onPick?.(s); setOpen(false); }} />
            <p className="mt-3 text-[11px] text-[#19324A]/45 text-center">Based on our store size guide. It's a guide, not gospel — easy exchanges if it's not quite right.</p>
          </div>
        </div>
      )}
    </>
  );
}
